import React, { useState, useEffect, useRef } from 'react'
import messages from './messages'
import { FormattedMessage, useIntl } from 'react-intl'
import { Full as Loading } from 'components/Loading'
import { setTheme, applyTheme } from 'themes/utils'
import { Button, Card } from '@arnsbogroup/react-voicearchive'

const PublicLiveSessionRedirectPage = ({ match }) => {
  const hashId = match.params.hashId
  const { formatMessage } = useIntl()

  const [loading, setLoading] = useState(false)
  const [redirectUrl, setRedirectUrl] = useState(null)
  const [meetingName, setMeetingName] = useState(null)
  const [countdown, setCountdown] = useState(null)
  const [brand, setBrand] = useState(null)

  const [showMicTest, setShowMicTest] = useState(false)
  const [micStatus, setMicStatus] = useState(null)
  const canvasRef = useRef(null)
  const animationRef = useRef(null)
  const audioStreamRef = useRef(null)

  useEffect(() => {
    if (!hashId) {
      return
    }

    setLoading(true)
    fetch(`/rest/v2/livesession/${hashId}`)
      .then((response) => response.json())
      .then(({ data, success, errors }) => {
        if (!success && errors) {
          toastr.error(errors.join(', '))
        }

        if (data) {
          setRedirectUrl(data.redirect)
          setMeetingName(data.meetingName)

          if (data.brand) {
            setBrand(data.brand)
            data.brand.theme ? setTheme(data.brand.theme) : applyTheme('base')
          }

          if (data.redirectImmediately) {
            window.location.href = data.redirect
            return
          }

          if (data.startsAt && data.minutesBeforeJoinable) {
            const joinTime = data.startsAt * 1000 - data.minutesBeforeJoinable * 60 * 1000
            const now = Date.now()
            const remainingTime = Math.max(0, Math.floor((joinTime - now) / 1000))
            setCountdown(remainingTime)
          }
        } else {
          applyTheme('base')
        }

        setLoading(false)
      })
      .catch((error) => toastr.error(error))
  }, [hashId])

  useEffect(() => {
    if (!countdown || countdown <= 0) {
      return
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          window.location.href = redirectUrl
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [countdown, redirectUrl])

  const startMicTest = async () => {
    try {
      setMicStatus('requesting')
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      audioStreamRef.current = stream

      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const micSource = audioContext.createMediaStreamSource(stream)
      const analyserNode = audioContext.createAnalyser()
      analyserNode.fftSize = 256
      micSource.connect(analyserNode)

      setMicStatus('success')
      drawVisualizer(analyserNode)
    } catch (err) {
      setMicStatus('error')
    }
  }

  const stopMicTest = () => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop())
      audioStreamRef.current = null
    }
    cancelAnimationFrame(animationRef.current)
    setMicStatus(null)
  }

  const drawVisualizer = (analyserNode) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const bufferLength = analyserNode.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)

    const resizeCanvas = () => {
      canvas.width = 100
      canvas.height = 100
    }
    resizeCanvas()

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw)
      analyserNode.getByteFrequencyData(dataArray)

      const { width, height } = canvas
      const centerX = width / 2
      const centerY = height / 2
      const baseRadius = width / 3

      ctx.clearRect(0, 0, width, height)

      ctx.beginPath()
      ctx.arc(centerX, centerY, baseRadius, 0, Math.PI * 2)

      const tempEl = document.createElement('div')
      tempEl.className = 'text-primary'
      document.body.appendChild(tempEl)

      const primaryColor = getComputedStyle(tempEl).color
      document.body.removeChild(tempEl)
      ctx.strokeStyle = primaryColor

      ctx.lineWidth = 2
      ctx.stroke()

      for (let i = 0; i < bufferLength; i += 4) {
        let value = dataArray[i]
        value = Math.min(255, value * 1.8)
        const angle = (i / bufferLength) * 2 * Math.PI
        const barLength = 4 + (value / 255) * 15
        const x1 = centerX + Math.cos(angle) * baseRadius
        const y1 = centerY + Math.sin(angle) * baseRadius
        const x2 = centerX + Math.cos(angle) * (baseRadius + barLength)
        const y2 = centerY + Math.sin(angle) * (baseRadius + barLength)

        ctx.beginPath()
        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y2)
        ctx.strokeStyle = primaryColor.replace('rgb', 'rgba').replace(')', `, ${0.5 + (value / 255) * 0.5})`)
        ctx.lineWidth = 2
        ctx.stroke()
      }
    }

    draw()

    return () => cancelAnimationFrame(animationRef.current)
  }

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return [hours, minutes, seconds]
      .map((unit) => String(unit).padStart(2, '0'))
      .join(':')
  }

  if (loading || !hashId) {
    return <Loading />
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-6 space-y-6">

      <Card className="w-full max-w-lg p-8 bg-white shadow-lg rounded-lg text-center">
        {redirectUrl ? (
          <>
            {brand && (
              <img
                src={brand.logo || brand.logo_dark}
                alt="Brand Logo"
                className="mx-auto w-32 mb-6"
              />
            )}

            <h1 className="text-xl font-bold text-gray-800 mb-4">{meetingName}</h1>

            <p className="text-gray-600 mb-6 whitespace-pre-wrap leading-relaxed">
              <FormattedMessage {...messages.liveSessionPreferences} />
            </p>

            {countdown > 0 ? (
              <p className="text-gray-600 text-xs mb-4">
                <FormattedMessage {...messages.redirectCountdown} values={{ time: formatTime(countdown) }} />
              </p>
            ) : (
              <p className="text-gray-600 mb-4">
                <FormattedMessage {...messages.waitingMessage} />
              </p>
            )}

            <Button
              disabled={countdown > 0}
              onClick={() => (window.location.href = redirectUrl)}
              className="w-full"
              title={countdown > 0 ? formatMessage(messages.buttonDisabledHint) : ''}
            >
              <FormattedMessage {...messages.joinNow} />
            </Button>
          </>
        ) : (
          <p className="text-gray-600">
            <FormattedMessage {...messages.liveSessionNotFound} />
          </p>
        )}
      </Card>

      <Button
        variant="outline"
        onClick={() => {
          if (showMicTest) stopMicTest()
          setShowMicTest((showMicTest) => !showMicTest)
          if (!showMicTest) startMicTest()
        }}
      >
        <FormattedMessage {...(showMicTest ? messages.hideMicTest : messages.testMic)} />
      </Button>

      {showMicTest && (
        <Card className="w-full max-w-md p-6 bg-gray-50 shadow rounded-lg text-center space-y-5 border border-gray-200">
          <h3 className="font-semibold text-gray-700 text-lg">
            <FormattedMessage {...messages.micTestTitle} />
          </h3>
          <p className="mb-6 text-gray-600 text-left text-sm">
            <FormattedMessage
              {...messages.allowMicrophoneAccessInstructions}
              values={{
                strong: chunks => <strong>{chunks}</strong>,
                ol: chunks => <ol className="list-decimal ml-5">{chunks}</ol>,
                li: chunks => <li>{chunks}</li>,
              }}
            />
          </p>

          <canvas
            ref={canvasRef}
            className="mx-auto mt-4"
            style={{ width: '100px', height: '100px' }}
          />

          {micStatus === 'requesting' && (
            <p className="text-primary text-sm">
              <FormattedMessage {...messages.requestingMic} />
            </p>
          )}
          {micStatus === 'success' && (
            <p className="text-positive text-sm">
              <FormattedMessage {...messages.micWorking} />
            </p>
          )}
          {micStatus === 'error' && (
            <p className="text-negative text-sm">
              <FormattedMessage {...messages.micError} />
            </p>
          )}
        </Card>
      )}
    </div>
  )
}

export default PublicLiveSessionRedirectPage
